import { CommonModule } from '@angular/common';
import { Component, ElementRef, inject, Input, OnInit, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { AccordionModule } from 'primeng/accordion';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { DividerModule } from 'primeng/divider';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { RadioButtonModule } from 'primeng/radiobutton';
import { TabsModule } from 'primeng/tabs';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { Subscription } from 'rxjs';

import { BlurhashImageDirective } from '@offconon/shared/ui/image-blurhash';
import {
  AbstractFilterComponent,
  setSearchParams,
  getSearchResults,
  selectElasticSearchBoxSearchResults,
  selectElasticSearchBoxBusinessSearchResults,
  getLoadingStatus,
  getPaginatedSearchResults,
} from '@offconon/shared/ui/search-box';
import { ThemeService } from '@offconon/shared/ui/theme-switch';
import { AppHelperService } from '@offconon/shared/utils/services/app-helper';
import { PlatformService } from '@offconon/shared/utils/services/platform';

import { ElasticSearchBoxFeatureModule } from '../elastic-search-box-feature.module';

@Component({
  selector: 'offconon-elastic-advanced-search',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    InputTextModule,
    RadioButtonModule,
    ButtonModule,
    TranslateModule,
    DividerModule,
    MatIcon,
    AccordionModule,
    ToggleSwitchModule,
    IconFieldModule,
    InputIconModule,
    AutoCompleteModule,
    DialogModule,
    ElasticSearchBoxFeatureModule,
    TabsModule,
    MessageModule,
    ProgressSpinnerModule,
    BlurhashImageDirective,
  ],
  templateUrl: './elastic-advanced-search.component.html',
  styles: [
    `
      .cat-img {
        width: 5rem;
        height: 5rem;
        object-fit: cover;
      }
    `,
  ],
})
export class ElasticAdvancedSearchComponent extends AbstractFilterComponent<any> implements OnInit {
  @Input() parameters: any;
  @Input() showDialog = false;
  @Input() withDialog = true;
  @Input() platform: string; //'core' || 'erp'
  @ViewChild('moreItem', { static: false })
  private loadMoreDiv: ElementRef<HTMLDivElement>;
  isLoadMoreDivScrolledIntoView: boolean;

  private themeService = inject(ThemeService);
  private platformService = inject(PlatformService);
  private appHelperService = inject(AppHelperService);

  private subscription: Subscription;
  private subscriptionLoading: Subscription;

  isMobileView = false;

  recent_search_parameters: any = {};

  entityMessageConfig: Record<string, { label: string; severity: string }> = {
    user: { label: 'User', severity: 'warn' },
    ad: { label: 'Ads', severity: 'info' },
    business: { label: 'Business', severity: 'success' },
    app: { label: 'App', severity: 'error' },
    catalog: { label: 'Catalog', severity: 'secondary' },
    prospect: { label: 'Prospect', severity: 'info' },
    co_worker: { label: 'Co worker', severity: 'success' },
  };
  tabList: any[] = [];
  loading = false;
  page = 1;
  pageSize = 10;

  selectedTabIndex = 0;
  searchResults: any;
  searchQuery: any = '';
  searchData: any;
  result: any;
  statusType: 'all' | 'status_filtered' = 'all';
  typeHelper = {
    is_ads: 'ad',
    is_apps: 'app',
    is_businesses: 'business',
    is_catalogs: 'catalog',
    is_members: 'user',
    is_co_workers: 'co_worker',
    is_prospects: 'prospect',
  } as const;

  ngOnInit() {
    document.addEventListener('scroll', this.scroll, true);
    if (!this.platform) {
      this.platformService.platformObservable.subscribe({
        next: (platform) => {
          this.platform = platform;
        },
      });
    }
    this.initForm();
    this.setTabList();
    this.subscriptionLoading = this.store$.select(getLoadingStatus).subscribe((loading) => {
      this.loading = loading;
    });
    if (this.platform === 'core') {
      this.subscription = this.store$
        .select(selectElasticSearchBoxSearchResults)
        .subscribe((res) => {
          if (res && res.results) {
            this.result = res;
            this.searchResults = this.setSearchData(res.results);
          }
        });
    }
    if (this.platform === 'erp') {
      this.subscription = this.store$
        .select(selectElasticSearchBoxBusinessSearchResults)
        .subscribe((res) => {
          if (res && res.results) {
            this.searchResults = this.setSearchData(res.results);
          }
        });
    }
    //todo from store
    /*this.recent_search_parameters = {
      keywords: [
        {
          label: 'How to be billionaire',
          id: 1,
        },
        {
          label: 'hello',
          id: 2,
        },
        {
          label: 'sell something',
          id: 3,
        },
        {
          label: 'Habibi come to Dubai',
          id: 4,
        },
      ],
    };*/
    this.themeService.mobileView.subscribe((isMobileView) => {
      this.isMobileView = isMobileView;
    });
  }
  scroll = () => {
    this.checkIfInView();
  };

  checkIfInView() {
    if (!this.loading) {
      const rect = this.loadMoreDiv?.nativeElement.getBoundingClientRect();
      const isInView = rect?.top >= 0 && rect.bottom <= window.innerHeight;

      this.isLoadMoreDivScrolledIntoView = isInView;
      if (isInView && !this.loading) {
        this.loadMore();
      }
    }
  }
  loadMore() {
    if (this.page > this.result.total_pages) {
      this.loading = false;
      return;
    }
    this.page++;
    this.store$.dispatch(
      getPaginatedSearchResults({
        searchJson: this.searchData,
        platform: this.platform,
        page: this.page,
        pageSize: this.pageSize,
      }),
    );
  }

  setTabList() {
    this.tabList = [
      { value: 0, label: 'All', show: true },
      { value: 1, label: 'Ads', show: true },
      { value: 2, label: 'Businesses', show: this.platform === 'core' },
      { value: 3, label: 'Apps', show: this.platform === 'core' },
      { value: 4, label: 'Members', show: this.platform === 'core' },
      { value: 5, label: 'Co workers', show: this.platform === 'erp' },
      { value: 6, label: 'Prospects', show: this.platform === 'erp' },
      { value: 7, label: 'Other', show: this.platform === 'core' },
    ];
  }
  onTabChange(index: number) {
    this.searchResults = [];
    this.page = 1;
    this.selectedTabIndex = index;

    this.form.patchValue({
      is_ads: false,
      is_businesses: false,
      is_catalogs: false,
      is_apps: false,
      is_members: false,
      is_co_workers: false,
      is_prospects: false,
    });

    switch (index) {
      case 0:
        this.form.patchValue({
          is_ads: true,
          is_businesses: this.platform === 'erp' ? false : true,
          is_catalogs: this.platform === 'erp' ? false : true,
          is_apps: this.platform === 'erp' ? false : true,
          is_members: this.platform === 'erp' ? false : true,
          is_co_workers: this.platform === 'erp' ? true : false,
          is_prospects: this.platform === 'erp' ? true : false,
        });
        break;
      case 1:
        this.form.patchValue({ is_ads: true });
        break;
      case 2:
        this.form.patchValue({ is_businesses: true });
        break;
      case 3:
        this.form.patchValue({ is_apps: true });
        break;
      case 4:
        this.form.patchValue({ is_members: true });
        break;
      case 5:
        this.form.patchValue({ is_co_workers: true });
        break;
      case 6:
        this.form.patchValue({ is_prospects: true });
        break;
      case 7:
        this.form.patchValue({ is_catalogs: true });
        break;
    }
    this.setSearchJson();
    if (this.form.value['translations'] && this.form.value['translations'].length > 2) {
      this.loading = true;
      setTimeout(() => {
        this.submit();
      }, 500);
    }
  }

  initForm() {
    this.form = this.fb.group({
      translations: [''],
      statusType: ['all'],
      is_ads: [true],
      is_businesses: [this.platform === 'erp' ? false : true],
      is_catalogs: [this.platform === 'erp' ? false : true],
      is_apps: [this.platform === 'erp' ? false : true],
      is_members: [this.platform === 'erp' ? false : true],
      is_co_workers: [this.platform === 'erp' ? true : false],
      is_prospects: [this.platform === 'erp' ? true : false],
    });
    this.setSearchJson();
    this.form.valueChanges.subscribe(() => {
      this.checkIsAll();
    });
  }

  checkIsAll() {
    if (this.platform === 'core') {
      const { is_ads, is_businesses, is_catalogs, is_apps, is_members } = this.form.value;

      if (is_ads && is_businesses && is_catalogs && is_apps && is_members) {
        this.form.patchValue({ statusType: 'all' }, { emitEvent: false });
      } else {
        this.form.patchValue({ statusType: 'status_filtered' }, { emitEvent: false });
      }
    }
    if (this.platform === 'erp') {
      const {
        is_ads,
        is_businesses,
        is_catalogs,
        is_apps,
        is_members,
        is_co_workers,
        is_prospects,
      } = this.form.value;

      if (
        is_ads &&
        is_businesses &&
        is_catalogs &&
        is_apps &&
        is_members &&
        is_co_workers &&
        is_prospects
      ) {
        this.form.patchValue({ statusType: 'all' }, { emitEvent: false });
      } else {
        this.form.patchValue({ statusType: 'status_filtered' }, { emitEvent: false });
      }
    }
  }

  setSearchJson() {
    let formData = this.form.value;
    this.searchJson = {};
    this.searchData = { query: '', entity_type_list: [] };
    for (const key in formData) {
      if (formData.hasOwnProperty(key)) {
        const elem = formData[key];
        switch (key) {
          case 'translations':
            this.searchJson[key] = elem;
            this.searchData.query = elem;
            break;

          case 'page':
          case 'page_size':
            break;
          default:
            this.searchJson[key] = elem;
            if (elem) {
              const typedKey = key as keyof typeof this.typeHelper;

              if (typedKey in this.typeHelper) {
                this.searchData.entity_type_list?.push(this.typeHelper[typedKey]);
              }
            }

            break;
        }
      }
    }
    this.store$.dispatch(setSearchParams({ searchJson: this.searchData }));
  }

  submit() {
    if (this.platform === 'core' && this.searchData.query.length > 2) {
      this.loading = true;
      this.store$.dispatch(
        getSearchResults({
          searchJson: this.searchData,
          platform: this.platform,
          page: this.page,
          pageSize: this.pageSize,
        }),
      );
    }
    if (this.platform === 'erp' && this.searchData.query.length > 2) {
      this.loading = true;
      this.store$.dispatch(
        getSearchResults({
          searchJson: this.searchData,
          platform: this.platform,
          page: this.page,
          pageSize: this.pageSize,
        }),
      );
    }
  }

  setSearchData(datas: any) {
    let showData: any[] = [];
    datas.forEach((element: any) => {
      let temp: any = {
        entity_type: '',
        name: '',
        link: '',
        image: '',
        description: '',
      };
      if (element.entity_type === 'user') {
        temp.entity_type = element.entity_type;
        temp.link = element.entity_data.username ? '/profile/' + element.entity_data.username : '/';
        temp.name = element.entity_data.full_name;
        temp.username = element.entity_data.username;
        temp.image = element.entity_data.avatar_path;
      }
      if (element.entity_type === 'co_worker') {
        temp.entity_type = 'co_worker';
        temp.link = element.entity_data.co_worker_id
          ? '/co-workers/profile/' + element.entity_data.co_worker_id
          : '/';
        temp.name = element.entity_data.full_name;
        temp.username = element.entity_data.username;
        temp.image = element.entity_data.avatar_path;
      }
      if (element.entity_type === 'prospect') {
        temp.entity_type = element.entity_type;
        temp.link = element.entity_data.id ? '/crm/contact-details/' + element.entity_data.id : '/';
        temp.name = element.entity_data.full_name
          ? element.entity_data.full_name
          : element.entity_data.note_name;
        temp.image = element.entity_data.avatar_path;
      }
      if (element.entity_type === 'app') {
        temp.entity_type = element.entity_type;
        temp.link = element.entity_data.link
          ? element.entity_data.link.toLowerCase().replace(/\s+/g, '-')
          : '/';
        temp.name = element.entity_data.keyword.translations[0].translation;
        temp.description = element.entity_data.keyword.translations[0].description;
        temp.image = element.entity_data.cropped_cover.file_path;
        temp.blur_hash = element.entity_data.cropped_cover.blur_hash;
      }
      if (element.entity_type === 'ad') {
        temp.entity_type = element.entity_type;
        if (element.entity_data?.id && element.entity_data?.long_description?.name) {
          temp.link =
            '/ad-details/' +
            element.entity_data.id +
            '/' +
            element.entity_data.long_description.name;
        } else {
          temp.link = '/';
        }

        temp.name = element.entity_data.keyword.translation;
        temp.image = element.entity_data.cover_path;
      }
      if (element.entity_type === 'business') {
        temp.entity_type = element.entity_type;
        temp.link = element.entity_data.business_mention_name
          ? '/business-profile/' + element.entity_data.business_mention_name
          : '/';
        temp.name = element.entity_data.business_name;
        temp.image = element.entity_data.business_avatar_path;
      }
      if (element.entity_type === 'catalog') {
        temp.entity_type = element.entity_type;
        temp.link = element.entity_id
          ? '/' +
            this.appHelperService.getAppConfig(element.entity_data.catalog_type)?.['app_url'] +
            '/catalog-identifier/' +
            element.entity_id
          : '/';
        temp.name = element.entity_data.name;
        temp.description = element.entity_data.description;
        temp.image = element.entity_data.cover_path;
      }
      showData.push(temp);
    });
    return showData;
  }
  onResultSelect(event: any) {
    this.router.navigate([event.link ?? '/']);
  }

  setToAll() {
    this.form.patchValue({
      statusType: ['all'],
      is_ads: true,
      is_businesses: this.platform === 'erp' ? false : true,
      is_catalogs: this.platform === 'erp' ? false : true,
      is_apps: this.platform === 'erp' ? false : true,
      is_members: this.platform === 'erp' ? false : true,
      is_co_workers: this.platform === 'erp' ? true : false,
      is_prospects: this.platform === 'erp' ? true : false,
    });
    this.setSearchJson();
  }
  resetCategory() {
    this.form.patchValue({
      statusType: ['all'],
      is_ads: true,
      is_businesses: this.platform === 'erp' ? false : true,
      is_catalogs: this.platform === 'erp' ? false : true,
      is_apps: this.platform === 'erp' ? false : true,
      is_members: this.platform === 'erp' ? false : true,
      is_co_workers: this.platform === 'erp' ? true : false,
      is_prospects: this.platform === 'erp' ? true : false,
      translations: '',
    });
    this.setSearchJson();
  }
}
